import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { useAuth } from "@/auth/useAuth";
import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { env } from "@/config/env";
import { ApiError } from "@/lib/api/client";
import { requestPasswordReset } from "@/services/auth";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notice =
    typeof location.state === "object" &&
    location.state &&
    "notice" in location.state &&
    typeof location.state.notice === "string"
      ? location.state.notice
      : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      const result = await login(parsed.data.email, parsed.data.password);
      if (result.status === "needs_password_reset") {
        await requestPasswordReset({ email: result.email });
        navigate("/reset-password", {
          replace: true,
          state: { email: result.email, notice: result.message },
        });
        return;
      }
      navigate("/applications", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      title="Sign in"
      description={`${env.appName} — use the credentials provided by Fincobox ops.`}
    >
      <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
        {notice ? (
          <Alert tone="success">{notice}</Alert>
        ) : null}
        {error ? <Alert>{error}</Alert> : null}
        <Input
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
          placeholder="you@partner.com"
        />
        <Input
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
          placeholder="••••••••"
        />
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            state={{ email }}
            className="text-sm font-medium text-brand hover:text-brand-strong"
          >
            Forgot password?
          </Link>
        </div>
        <Button type="submit" className="w-full" loading={loading}>
          Continue
        </Button>
      </form>
    </AuthSplitLayout>
  );
}
